import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Home, Search, Menu, X, Phone, Mail, MapPin, Star, ArrowRight, 
  Heart, BedDouble, Bath, Square, Filter, ChevronDown, Calendar,
  MessageCircle, CheckCircle, Users, Building, DollarSign, Clock,
  Facebook, Linkedin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { SiteConfigProvider, useSiteConfig } from "@/context/SiteConfigContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default Agent Info (will be overridden by dynamic config)
const AGENT_INFO = {
  name: "Melanie Bobby",
  title: "Broker, C21 Northern Advantage",
  phone: "605-216-3130",
  email: "melanie@advnorthern.com",
  tagline: "Investing in You",
  bio: `I'm Melanie Bobby, a proud Broker with C21 Northern Advantage, and I'm honored to serve clients in Aberdeen and the surrounding communities. My approach to real estate is rooted in the values that guide my life every day - imperfectly following Jesus, loving my family, and a genuine commitment to helping others succeed.

For me, real estate is about far more than properties and transactions. It's about people, relationships, and helping families find the place where their lives will grow and thrive. Whether you're purchasing your first home, upgrading for your growing family, downsizing, or exploring investment opportunities, I'm dedicated to guiding you through the process with honesty, patience, and strong market knowledge.

Real estate is my profession, but serving people is my passion. When you work with me, you're not just another transaction - you're someone I'm committed to investing in.`,
  portrait: "https://customer-assets.emergentagent.com/job_agent-suite-7/artifacts/hxo0e7nl_2406905_normal.jpg",
  socials: {
    facebook: "https://www.facebook.com/Melanie.Bobby",
    linkedin: "https://www.linkedin.com/in/melanie-bobby-0b188825",
    realtor: "https://www.realtor.com/realestateagents/5d7e488ca525f000136e5738"
  }
};

// Helper hook to get agent info from config (supports both flat and nested Neptune Monkey format)
const useAgentInfo = () => {
  const { config } = useSiteConfig();
  
  // Get primary agent (first in agents array or from flat fields)
  const primaryAgent = config?.agents?.[0] || {};
  const contactInfo = config?.contact_info || {};
  const images = config?.images || {};
  
  return {
    // Agent info - try nested format first, then flat fields, then defaults
    name: primaryAgent.name || config?.agent_name || AGENT_INFO.name,
    title: primaryAgent.title || config?.agent_title || AGENT_INFO.title,
    bio: primaryAgent.bio || config?.agent_bio || AGENT_INFO.bio,
    portrait: primaryAgent.photo || config?.agent_photo || AGENT_INFO.portrait,
    
    // Contact info - try nested format first, then flat fields
    phone: contactInfo.phone || config?.phone || AGENT_INFO.phone,
    email: contactInfo.email || config?.email || AGENT_INFO.email,
    address: contactInfo.address || `${config?.city || 'Aberdeen'}, ${config?.state || 'SD'}`,
    
    // Other fields
    tagline: config?.tagline || AGENT_INFO.tagline,
    
    // Social links
    socials: {
      facebook: config?.facebook || AGENT_INFO.socials.facebook,
      linkedin: config?.linkedin || AGENT_INFO.socials.linkedin,
      realtor: config?.realtor_com || AGENT_INFO.socials.realtor
    },
    
    // Images - try nested format first
    heroImage: images.hero || IMAGES.hero,
    aboutBanner: images.about_banner || IMAGES.aboutBanner,
    logo: images.logo || null,
    
    // About section
    aboutHeading: config?.about_heading || config?.about_headline || "Investing in You",
    aboutDescription: config?.about_description || config?.about_content || "Real estate is more than just a transaction...",
    
    // URLs
    mls_search_url: config?.mls_search_url || "https://www.hubcityhomes4sale.com/map/",
    market_leader_email: config?.market_leader_email || "melanie@hubcityhomes4sale.com",
    hero_headline: config?.hero_headline || "Find Your Perfect Home in Aberdeen",
    hero_subheadline: config?.hero_subheadline || "Expert guidance for buying and selling in Hub City and surrounding South Dakota communities"
  };
};

// Images (defaults - can be overridden by config)
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_agent-suite-7/artifacts/zws7h63r_Option%202.png",
  community: "https://images.unsplash.com/photo-1773020678322-2a17f2d22007?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxzbWFsbCUyMHRvd24lMjBtYWluJTIwc3RyZWV0JTIwc3VubnklMjBkYXklMjBmcmllbmRseXxlbnwwfHx8fDE3NzM4ODQ2ODd8MA&ixlib=rb-4.1.0&q=85",
  family: "https://images.unsplash.com/photo-1767858899059-ffb178e8a6ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxmYW1pbHklMjBwbGF5aW5nJTIwaW4lMjBnYXJkZW58ZW58MHx8fHwxNzczODg0Njk0fDA&ixlib=rb-4.1.0&q=85",
  interior: "https://images.unsplash.com/photo-1768144092684-c1a5dd6c7aad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjB3YXJtJTIwYmVpZ2UlMjBkZXNpZ258ZW58MHx8fHwxNzczODg0Njg4fDA&ixlib=rb-4.1.0&q=85",
  prairie: "https://images.unsplash.com/photo-1632902879171-cf891ccacb67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxwcmFpcmllJTIwbGFuZHNjYXBlJTIwZ29sZGVuJTIwaG91ciUyMHNvdXRoJTIwZGFrb3RhfGVufDB8fHx8MTc3Mzg4NDY5Nnww&ixlib=rb-4.1.0&q=85",
  // Melanie's branded images
  aboutBanner: "https://customer-assets.emergentagent.com/job_agent-suite-7/artifacts/61s2wnga_Option%204.png",
  melanieWithSign: "https://customer-assets.emergentagent.com/job_agent-suite-7/artifacts/n54kuazu_IMG_20260318_211818.png"
};

// Realtor.com Icon Component
const RealtorIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v2h-2V7zm0 4h2v6h-2v-6z"/>
    <text x="50%" y="55%" dominantBaseline="middle" textAnchor="middle" fontSize="8" fontWeight="bold">R</text>
  </svg>
);

// Social Links Component
const SocialLinks = ({ className = "", iconSize = "h-5 w-5" }) => (
  <div className={`flex items-center gap-3 ${className}`} data-testid="social-links">
    <a 
      href={AGENT_INFO.socials.facebook}
      target="_blank"
      rel="noopener noreferrer"
      className="p-2 rounded-full bg-white/10 hover:bg-primary/20 transition-colors group"
      aria-label="Facebook"
      data-testid="social-facebook"
    >
      <Facebook className={`${iconSize} text-white/70 group-hover:text-primary`} />
    </a>
    <a 
      href={AGENT_INFO.socials.linkedin}
      target="_blank"
      rel="noopener noreferrer"
      className="p-2 rounded-full bg-white/10 hover:bg-primary/20 transition-colors group"
      aria-label="LinkedIn"
      data-testid="social-linkedin"
    >
      <Linkedin className={`${iconSize} text-white/70 group-hover:text-primary`} />
    </a>
    <a 
      href={AGENT_INFO.socials.realtor}
      target="_blank"
      rel="noopener noreferrer"
      className="p-2 rounded-full bg-white/10 hover:bg-primary/20 transition-colors group"
      aria-label="Realtor.com"
      data-testid="social-realtor"
    >
      <svg className={`${iconSize} text-white/70 group-hover:text-primary`} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18l6.9 3.45L12 11.08 5.1 7.63 12 4.18zM4 8.82l7 3.5v7.36l-7-3.5V8.82zm9 10.86v-7.36l7-3.5v7.36l-7 3.5z"/>
      </svg>
    </a>
  </div>
);

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const agentInfo = useAgentInfo();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Properties", path: agentInfo.mls_search_url, external: true },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" }
  ];

  return (
    <nav 
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-nav shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container-main">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Home className="h-8 w-8 text-primary" />
            <div className="flex flex-col">
              <span className="font-heading text-xl font-semibold text-secondary">
                {agentInfo.name}
              </span>
              <span className="text-xs text-muted-foreground">Hub City Homes | Aberdeen, SD</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    isScrolled ? "text-secondary" : "text-secondary"
                  }`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors hover:text-primary ${
                    location.pathname === link.path
                      ? "text-primary"
                      : isScrolled ? "text-secondary" : "text-secondary"
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href={`tel:${AGENT_INFO.phone}`}
              className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              data-testid="phone-link"
            >
              <Phone className="h-4 w-4" />
              {AGENT_INFO.phone}
            </a>
            <Link to="/contact">
              <Button 
                data-testid="nav-contact-btn"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6"
              >
                Get In Touch
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <div className="flex flex-col gap-6 mt-8">
                {navLinks.map((link) => (
                  link.external ? (
                    <a
                      key={link.path}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-lg font-medium text-secondary"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`text-lg font-medium ${
                        location.pathname === link.path ? "text-primary" : "text-secondary"
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
                ))}
                <hr className="my-4" />
                <a 
                  href={`tel:${AGENT_INFO.phone}`}
                  className="flex items-center gap-2 text-muted-foreground"
                >
                  <Phone className="h-5 w-5" />
                  {AGENT_INFO.phone}
                </a>
                <a 
                  href={`mailto:${AGENT_INFO.email}`}
                  className="flex items-center gap-2 text-muted-foreground"
                >
                  <Mail className="h-5 w-5" />
                  {AGENT_INFO.email}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

// Property Card Component
const PropertyCard = ({ property }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Link to Market Leader for real listings
  const handleClick = () => {
    if (property.ml_url) {
      window.open(property.ml_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Handle both images array and single image field
  const imageUrl = property.images?.[0] || property.image || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800";

  return (
    <div 
      data-testid={`property-card-${property.id}`}
      className="card-property property-card cursor-pointer group"
      onClick={handleClick}
    >
      {/* Image */}
      <div className="relative aspect-property overflow-hidden">
        <img
          src={imageUrl}
          alt={property.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {/* Status Badge */}
        {property.status === "Active" && property.is_featured && (
          <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
            Featured
          </Badge>
        )}
        {property.status === "Pending" && (
          <Badge className="absolute top-4 left-4 badge-pending">Pending</Badge>
        )}
        {property.status === "Sold" && (
          <Badge className="absolute top-4 left-4 badge-sold">Sold</Badge>
        )}
        {/* Favorite Button */}
        <button 
          data-testid={`favorite-btn-${property.id}`}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            toast.success("Added to favorites!");
          }}
        >
          <Heart className="h-5 w-5 text-secondary" />
        </button>
      </div>

      {/* Content */}
      <div className="p-5">
        <p className="price-display mb-2">{formatPrice(property.price)}</p>
        <h3 className="font-heading text-lg font-semibold text-secondary mb-2 line-clamp-1">
          {property.title}
        </h3>
        <p className="text-muted-foreground text-sm mb-4 flex items-center gap-1">
          <MapPin className="h-4 w-4" />
          {property.address}, {property.city}, {property.state}
        </p>
        
        {/* Property Details */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground border-t border-border pt-4">
          <span className="flex items-center gap-1">
            <BedDouble className="h-4 w-4" />
            {property.beds} Beds
          </span>
          <span className="flex items-center gap-1">
            <Bath className="h-4 w-4" />
            {property.baths} Baths
          </span>
          <span className="flex items-center gap-1">
            <Square className="h-4 w-4" />
            {(property.sqft || 0).toLocaleString()} sqft
          </span>
        </div>
        
        {/* MLS Link indicator */}
        {property.ml_url && (
          <div className="mt-3 pt-3 border-t border-border">
            <span className="text-xs text-primary flex items-center gap-1">
              <ArrowRight className="h-3 w-3" />
              Click to view on MLS
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Testimonial Card Component
const TestimonialCard = ({ testimonial }) => (
  <div data-testid={`testimonial-${testimonial.id}`} className="testimonial-card">
    <div className="flex gap-1 mb-4">
      {[...Array(testimonial.rating)].map((_, i) => (
        <Star key={i} className="h-5 w-5 fill-primary text-primary" />
      ))}
    </div>
    <p className="text-secondary mb-6 italic">"{testimonial.content}"</p>
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
        <Users className="h-6 w-6 text-muted-foreground" />
      </div>
      <div>
        <p className="font-semibold text-secondary">{testimonial.name}</p>
        <p className="text-sm text-muted-foreground">{testimonial.location}</p>
      </div>
    </div>
  </div>
);

// Market Leader Search CTA Component
const MLSearchCTA = ({ variant = "primary", className = "" }) => {
  const baseUrl = "https://www.hubcityhomes4sale.com";
  
  if (variant === "hero") {
    return (
      <div className={`bg-white rounded-2xl shadow-xl p-6 md:p-8 ${className}`}>
        <h3 className="font-heading text-2xl font-semibold text-secondary mb-3 text-center">
          Search Aberdeen Homes
        </h3>
        <p className="text-muted-foreground text-center mb-6">
          Browse all MLS listings in Aberdeen and surrounding areas
        </p>
        <div className="flex justify-center">
          <a 
            href={`${baseUrl}/map/`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center justify-center gap-2"
            data-testid="search-all-homes"
          >
            <Search className="h-5 w-5" />
            Search All Homes
          </a>
        </div>
      </div>
    );
  }
  
  // Compact variant for sidebar/smaller spaces
  return (
    <div className={`bg-primary/5 rounded-xl p-4 border border-primary/20 ${className}`}>
      <p className="font-medium text-secondary mb-3 text-sm">Search Properties</p>
      <a 
        href={`${baseUrl}/map/`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full btn-primary inline-flex items-center justify-center gap-2 text-sm py-2"
      >
        <Search className="h-4 w-4" />
        Browse MLS Listings
      </a>
    </div>
  );
};

// Lead Capture Form Component - Direct link to Market Leader
const LeadCaptureForm = ({ source = "website", propertyInterest = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to database and send email to Market Leader via SendGrid
      await axios.post(`${API}/leads`, {
        ...formData,
        source,
        property_interest: propertyInterest,
        lead_type: propertyInterest ? "property_inquiry" : "contact"
      });
      
      toast.success("Thank you! Melanie will be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-capture-form">
      <div>
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          data-testid="lead-name-input"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Your name"
          className="input-default mt-1"
        />
      </div>
      <div>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          data-testid="lead-email-input"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="your@email.com"
          className="input-default mt-1"
        />
      </div>
      <div>
        <Label htmlFor="phone">Phone</Label>
        <Input
          id="phone"
          type="tel"
          data-testid="lead-phone-input"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="(555) 123-4567"
          className="input-default mt-1"
        />
      </div>
      <div>
        <Label htmlFor="message">Message</Label>
        <Textarea
          id="message"
          data-testid="lead-message-input"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          placeholder="Tell me about your real estate goals..."
          className="input-default mt-1 min-h-[120px]"
        />
      </div>
      <Button 
        type="submit" 
        data-testid="lead-submit-btn"
        className="w-full btn-primary"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending..." : "Send Message"}
      </Button>
      <p className="text-xs text-center text-muted-foreground">
        Your message will be sent directly to Melanie
      </p>
    </form>
  );
};

// Styled Custom Form (backup/alternative)
const CustomLeadForm = ({ source = "website", propertyInterest = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        source,
        property_interest: propertyInterest,
        lead_type: propertyInterest ? "property_inquiry" : "contact"
      });
      
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="custom-lead-form">
      <div>
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          data-testid="lead-name-input"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Your name"
          className="input-default mt-1"
        />
      </div>
      <div>
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          data-testid="lead-email-input"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="your@email.com"
          className="input-default mt-1"
        />
      </div>
      <div>
        <Label htmlFor="phone">Phone</Label>
        <Input
          id="phone"
          type="tel"
          data-testid="lead-phone-input"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="(555) 123-4567"
          className="input-default mt-1"
        />
      </div>
      <div>
        <Label htmlFor="message">Message</Label>
        <Textarea
          id="message"
          data-testid="lead-message-input"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          placeholder="Tell me about your real estate goals..."
          className="input-default mt-1 min-h-[120px]"
        />
      </div>
      <Button 
        type="submit" 
        data-testid="lead-submit-btn"
        className="w-full btn-primary"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending..." : "Send Message"}
      </Button>
    </form>
  );
};

// Schedule Showing Component
const ScheduleShowingModal = ({ property, trigger }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    preferredDate: "",
    preferredTime: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `SHOWING REQUEST\nPreferred Date: ${formData.preferredDate}\nPreferred Time: ${formData.preferredTime}\n\nNotes: ${formData.message}`,
        property_interest: property?.title || "General Showing",
        lead_type: "showing_request",
        source: "schedule_showing"
      });
      
      toast.success("Showing request sent! Melanie will contact you to confirm.");
      setFormData({ name: "", email: "", phone: "", preferredDate: "", preferredTime: "", message: "" });
      setOpen(false);
    } catch (error) {
      toast.error("Something went wrong. Please try again or call directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate available time slots
  const timeSlots = [
    "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"
  ];

  // Get min date (tomorrow)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Schedule a Showing</DialogTitle>
          {property && (
            <p className="text-muted-foreground text-sm mt-1">
              {property.address}, {property.city}
            </p>
          )}
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="schedule-showing-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="showing-name">Name *</Label>
              <Input
                id="showing-name"
                data-testid="showing-name-input"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="showing-email">Email *</Label>
              <Input
                id="showing-email"
                type="email"
                data-testid="showing-email-input"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="showing-phone">Phone *</Label>
              <Input
                id="showing-phone"
                type="tel"
                data-testid="showing-phone-input"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="showing-date">Preferred Date *</Label>
              <Input
                id="showing-date"
                type="date"
                data-testid="showing-date-input"
                required
                min={minDate}
                value={formData.preferredDate}
                onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="showing-time">Preferred Time *</Label>
              <Select
                value={formData.preferredTime}
                onValueChange={(value) => setFormData({ ...formData, preferredTime: value })}
              >
                <SelectTrigger className="mt-1" data-testid="showing-time-select">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label htmlFor="showing-message">Notes (optional)</Label>
              <Textarea
                id="showing-message"
                data-testid="showing-message-input"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Any special requests or questions?"
                className="mt-1 min-h-[80px]"
              />
            </div>
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button 
              type="submit" 
              data-testid="schedule-showing-submit-btn"
              className="flex-1 btn-primary"
              disabled={isSubmitting || !formData.preferredTime}
            >
              <Calendar className="h-4 w-4 mr-2" />
              {isSubmitting ? "Sending..." : "Request Showing"}
            </Button>
          </div>
          
          <p className="text-xs text-center text-muted-foreground">
            Melanie will confirm your appointment within 24 hours
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Footer Component
const Footer = () => {
  const agentInfo = useAgentInfo();
  
  return (
  <footer className="footer-main py-16" data-testid="main-footer">
    <div className="container-main">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        {/* Brand */}
        <div className="col-span-1 md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Home className="h-8 w-8 text-primary" />
            <span className="font-heading text-2xl font-semibold text-white">
              {agentInfo.name}
            </span>
          </div>
          <p className="text-white/60 text-sm mb-2">Hub City Homes</p>
          <p className="text-white/80 text-2xl font-heading italic mb-4">
            "{agentInfo.tagline}"
          </p>
          <p className="text-white/60 max-w-md mb-6">
            Serving Aberdeen, SD and surrounding communities with honesty, patience, and strong market knowledge.
          </p>
          {/* Social Links */}
          <SocialLinks />
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-semibold text-white mb-4">Quick Links</h4>
          <div className="space-y-2">
            <a href={agentInfo.mls_search_url} target="_blank" rel="noopener noreferrer" className="block text-white/70 hover:text-primary transition-colors">
              Browse Properties
            </a>
            <Link to="/about" className="block text-white/70 hover:text-primary transition-colors">
              About {agentInfo.name.split(' ')[0]}
            </Link>
            <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors">
              Contact Us
            </Link>
          </div>
        </div>

        {/* Contact */}
        <div>
          <h4 className="font-semibold text-white mb-4">Contact</h4>
          <div className="space-y-3">
            <a 
              href={`tel:${agentInfo.phone}`}
              className="flex items-center gap-2 text-white/70 hover:text-primary transition-colors"
            >
              <Phone className="h-4 w-4" />
              {agentInfo.phone}
            </a>
            <a 
              href={`mailto:${agentInfo.email}`}
              className="flex items-center gap-2 text-white/70 hover:text-primary transition-colors"
            >
              <Mail className="h-4 w-4" />
              {agentInfo.email}
            </a>
            <p className="flex items-center gap-2 text-white/70">
              <MapPin className="h-4 w-4" />
              Aberdeen, SD
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-white/50 text-sm">
          © 2026 {agentInfo.name}, Century 21 Northern Advantage. All rights reserved.
        </p>
        <p className="text-white/50 text-sm">
          Aberdeen SD Real Estate • South Dakota Homes • Equal Housing Opportunity
        </p>
      </div>
    </div>
  </footer>
  );
};

// ============== PAGES ==============

// Home Page
const HomePage = () => {
  const navigate = useNavigate();
  const agentInfo = useAgentInfo();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useState({
    location: "",
    propertyType: "",
    priceRange: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [liveRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/properties/live`),
          axios.get(`${API}/testimonials?featured=true`)
        ]);
        
        // Live endpoint returns { source: "live", listings: [...] }
        const listings = liveRes.data.listings || [];
        setFeaturedProperties(listings);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchParams.propertyType) params.set("type", searchParams.propertyType);
    if (searchParams.priceRange) params.set("price", searchParams.priceRange);
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section with Branded Banner */}
      <section className="relative" data-testid="hero-section">
        {/* Banner Image */}
        <div className="w-full pt-20">
          <img 
            src={IMAGES.hero}
            alt={`${agentInfo.name} - Real Estate Broker - Century 21 Northern Advantage - Aberdeen SD`}
            className="w-full h-auto object-cover"
          />
        </div>
        
        {/* Search CTA */}
        <div className="bg-secondary py-8">
          <div className="container-main">
            <MLSearchCTA variant="hero" className="max-w-2xl mx-auto" />
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="section-padding bg-background" data-testid="featured-properties-section">
        <div className="container-main">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div>
              <p className="caption-text text-primary mb-2">Featured Listings</p>
              <h2 className="heading-lg text-secondary">Homes You'll Love</h2>
            </div>
            <a 
              href={agentInfo.mls_search_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="mt-4 md:mt-0 rounded-full" data-testid="view-all-properties-btn">
                View All Properties
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </a>
          </div>

          {loading ? (
            <div className="grid-properties">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-[400px]" />
              ))}
            </div>
          ) : featuredProperties.length > 0 ? (
            <div className="grid-properties">
              {featuredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          ) : (
            <div className="idx-placeholder py-16">
              <Building className="h-12 w-12 mx-auto mb-4 text-accent" />
              <h3 className="font-heading text-xl mb-2">IDX Integration Coming Soon</h3>
              <p className="text-muted-foreground">
                Live MLS listings will appear here once IDX is connected.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding bg-muted/30" data-testid="about-preview-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <p className="caption-text text-primary mb-2">Your Aberdeen Realtor</p>
              <h2 className="heading-lg text-secondary mb-6">Meet Melanie Bobby</h2>
              <p className="body-text mb-6">
                Real estate is my profession, but serving people is my passion. My approach is rooted in the values that guide my life every day - faith, family, and a genuine commitment to helping others succeed.
              </p>
              <p className="body-text mb-8">
                Whether you're purchasing your first home, upgrading for your growing family, downsizing, or exploring investment opportunities, I'm dedicated to guiding you through the process with honesty, patience, and strong market knowledge.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/about">
                  <Button className="btn-primary" data-testid="learn-more-btn">
                    Learn More About Me
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="btn-secondary" data-testid="get-in-touch-btn">
                    Get In Touch
                  </Button>
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img
                  src={AGENT_INFO.portrait}
                  alt="Melanie Bobby"
                  className="rounded-2xl shadow-xl w-full max-w-md mx-auto"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 hidden md:block">
                  <p className="font-heading text-2xl italic text-primary">"{AGENT_INFO.tagline}"</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats/Services */}
      <section className="py-12 bg-secondary" data-testid="stats-section">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: Home, label: "Homes Sold", value: "150+" },
              { icon: Users, label: "Happy Families", value: "200+" },
              { icon: Calendar, label: "Years Experience", value: "10+" },
              { icon: Star, label: "5-Star Reviews", value: "50+" }
            ].map((stat, i) => (
              <div key={i} className="text-white">
                <stat.icon className="h-8 w-8 mx-auto mb-3 text-primary" />
                <p className="font-heading text-3xl font-bold mb-1">{stat.value}</p>
                <p className="text-white/70 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="section-padding bg-background" data-testid="testimonials-section">
          <div className="container-main">
            <div className="text-center mb-12">
              <p className="caption-text text-primary mb-2">Client Stories</p>
              <h2 className="heading-lg text-secondary">What My Clients Say</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section 
        className="section-padding relative"
        style={{ backgroundImage: `url(${IMAGES.prairie})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-secondary/80" />
        <div className="container-main relative z-10 text-center">
          <h2 className="heading-lg text-white mb-6">Ready to Find Your Home?</h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Let's start your real estate journey together. Whether buying or selling, 
            I'm here to help every step of the way.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/properties">
              <Button className="btn-primary" data-testid="browse-listings-btn">
                Browse Listings
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 rounded-full px-8 py-4">
                Schedule a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Properties Page - Links to Market Leader MLS Search
const PropertiesPage = () => {
  const baseUrl = "https://www.hubcityhomes4sale.com";
  
  return (
    <div className="pt-20" data-testid="properties-page">
      {/* Header */}
      <section className="bg-muted/30 py-12">
        <div className="container-main">
          <h1 className="heading-lg text-secondary mb-4">Aberdeen Properties</h1>
          <p className="body-text max-w-2xl">
            Browse available homes in Aberdeen, SD and surrounding communities. 
            All listings powered by MLS data through Market Leader.
          </p>
        </div>
      </section>

      {/* Search Options */}
      <section className="section-padding">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Search All */}
            <a 
              href={`${baseUrl}/map/`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg hover:border-primary/30 transition-all group"
            >
              <Search className="h-10 w-10 text-primary mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                Search All Homes
              </h3>
              <p className="text-muted-foreground text-sm">
                Browse all available listings with filters for price, beds, baths, and more
              </p>
            </a>
            
            {/* Map Search */}
            <a 
              href={`${baseUrl}/map/`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg hover:border-primary/30 transition-all group"
            >
              <MapPin className="h-10 w-10 text-primary mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                Map Search
              </h3>
              <p className="text-muted-foreground text-sm">
                Find homes by location with our interactive map view
              </p>
            </a>
            
            {/* New Listings */}
            <a 
              href={`${baseUrl}/map/?sortby=listingdate`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-6 shadow-sm border border-border hover:shadow-lg hover:border-primary/30 transition-all group"
            >
              <Calendar className="h-10 w-10 text-primary mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                Newest Listings
              </h3>
              <p className="text-muted-foreground text-sm">
                See the most recently listed properties first
              </p>
            </a>
          </div>
          
          {/* Quick Search by Price */}
          <div className="bg-muted/30 rounded-2xl p-8">
            <h2 className="font-heading text-2xl font-semibold text-center mb-6">Search by Price Range</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Under $200K", min: 0, max: 200000 },
                { label: "$200K - $300K", min: 200000, max: 300000 },
                { label: "$300K - $500K", min: 300000, max: 500000 },
                { label: "$500K+", min: 500000, max: 0 }
              ].map((range, i) => (
                <a
                  key={i}
                  href={`${baseUrl}/map/?minprice=${range.min}${range.max ? `&maxprice=${range.max}` : ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white rounded-xl p-4 text-center hover:bg-primary hover:text-white transition-colors shadow-sm"
                >
                  <DollarSign className="h-6 w-6 mx-auto mb-2" />
                  <span className="font-medium">{range.label}</span>
                </a>
              ))}
            </div>
          </div>
          
          {/* Contact CTA */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              Can't find what you're looking for? Let me help!
            </p>
            <Link to="/contact">
              <Button className="btn-primary">
                <MessageCircle className="h-5 w-5 mr-2" />
                Contact Melanie
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Property Detail Page
const PropertyDetailPage = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await axios.get(`${API}/properties/${id}`);
        setProperty(response.data);
      } catch (error) {
        console.error("Error fetching property:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProperty();
  }, [id]);

  if (loading) {
    return (
      <div className="pt-20 section-padding">
        <div className="container-main">
          <div className="skeleton h-[500px] mb-8" />
          <div className="skeleton h-8 w-1/2 mb-4" />
          <div className="skeleton h-4 w-1/3" />
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="pt-20 section-padding">
        <div className="container-main text-center">
          <h1 className="heading-lg mb-4">Property Not Found</h1>
          <Link to="/properties">
            <Button className="btn-primary">Back to Properties</Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="pt-20" data-testid="property-detail-page">
      {/* Image Gallery */}
      <section className="bg-muted">
        <div className="container-main py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <img
              src={property.images?.[0] || "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800"}
              alt={property.title}
              className="w-full h-[400px] object-cover rounded-xl"
            />
            {property.images?.[1] && (
              <img
                src={property.images[1]}
                alt={property.title}
                className="w-full h-[400px] object-cover rounded-xl hidden md:block"
              />
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                  <p className="price-display text-4xl mb-2">{formatPrice(property.price)}</p>
                  <h1 className="heading-md text-secondary">{property.title}</h1>
                </div>
                {property.status !== "Active" && (
                  <Badge className={property.status === "Pending" ? "badge-pending" : "badge-sold"}>
                    {property.status}
                  </Badge>
                )}
              </div>

              <p className="text-muted-foreground flex items-center gap-2 mb-8">
                <MapPin className="h-5 w-5" />
                {property.address}, {property.city}, {property.state} {property.zip_code}
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-4 mb-8 p-6 bg-muted/30 rounded-xl">
                <div className="text-center">
                  <BedDouble className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <p className="font-bold text-xl">{property.beds}</p>
                  <p className="text-sm text-muted-foreground">Beds</p>
                </div>
                <div className="text-center">
                  <Bath className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <p className="font-bold text-xl">{property.baths}</p>
                  <p className="text-sm text-muted-foreground">Baths</p>
                </div>
                <div className="text-center">
                  <Square className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <p className="font-bold text-xl">{property.sqft.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">Sq Ft</p>
                </div>
                <div className="text-center">
                  <Calendar className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <p className="font-bold text-xl">{property.year_built || "N/A"}</p>
                  <p className="text-sm text-muted-foreground">Year Built</p>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="heading-md mb-4">About This Property</h2>
                <p className="body-text">{property.description}</p>
              </div>

              {/* Features */}
              {property.features?.length > 0 && (
                <div className="mb-8">
                  <h2 className="heading-md mb-4">Features</h2>
                  <div className="flex flex-wrap gap-2">
                    {property.features.map((feature, i) => (
                      <span key={i} className="feature-tag">
                        <CheckCircle className="h-4 w-4 mr-1 text-accent" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Property Details */}
              <div>
                <h2 className="heading-md mb-4">Property Details</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Property Type</span>
                    <span className="font-medium">{property.property_type}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">MLS #</span>
                    <span className="font-medium">{property.mls_number || "N/A"}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Lot Size</span>
                    <span className="font-medium">{property.lot_size || "N/A"}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Status</span>
                    <span className="font-medium">{property.status}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar - Contact Form */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-card rounded-2xl shadow-lg p-6 border border-border">
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={AGENT_INFO.portrait}
                    alt={AGENT_INFO.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-heading font-semibold text-lg">{AGENT_INFO.name}</p>
                    <p className="text-sm text-muted-foreground">{AGENT_INFO.title}</p>
                  </div>
                </div>

                {/* Schedule a Showing Button */}
                <ScheduleShowingModal 
                  property={property}
                  trigger={
                    <Button 
                      className="w-full btn-primary mb-4"
                      data-testid="schedule-showing-btn"
                    >
                      <Calendar className="h-5 w-5 mr-2" />
                      Schedule a Showing
                    </Button>
                  }
                />

                <div className="space-y-3 mb-6">
                  <a 
                    href={`tel:${AGENT_INFO.phone}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <Phone className="h-5 w-5 text-primary" />
                    <span>{AGENT_INFO.phone}</span>
                  </a>
                  <a 
                    href={`mailto:${AGENT_INFO.email}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <Mail className="h-5 w-5 text-primary" />
                    <span className="truncate">{AGENT_INFO.email}</span>
                  </a>
                </div>

                <div className="border-t border-border pt-6">
                  <p className="text-sm font-medium mb-4">Or send a message:</p>
                  <LeadCaptureForm 
                    source="property_detail"
                    propertyInterest={property.title}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => (
  <div className="pt-20 overflow-x-hidden" data-testid="about-page">
    {/* Hero Banner */}
    <section className="relative w-full overflow-hidden">
      <img 
        src={IMAGES.aboutBanner} 
        alt="Melanie Bobby - Real Estate Broker - Century 21 Northern Advantage"
        className="w-full h-auto object-cover object-center"
      />
    </section>

    {/* Bio */}
    <section className="section-padding">
      <div className="container-main">
        <div className="max-w-3xl">
          <p className="caption-text text-primary mb-2">About Me</p>
          <h1 className="heading-xl text-secondary mb-8">{AGENT_INFO.name}</h1>
          <p className="text-2xl font-heading italic text-primary mb-8">"{AGENT_INFO.tagline}"</p>
          
          {AGENT_INFO.bio.split('\n\n').map((paragraph, i) => (
            <p key={i} className="body-text mb-6">{paragraph}</p>
          ))}
          
          {/* Connect with Me - Social Links */}
          <div className="mt-12 p-6 bg-muted/30 rounded-xl">
            <h3 className="font-heading text-lg font-semibold mb-4">Connect With Me</h3>
            <div className="flex flex-wrap items-center gap-3">
              <a 
                href={AGENT_INFO.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2]/20 text-[#1877F2] transition-colors"
              >
                <Facebook className="h-5 w-5" />
                <span className="text-sm font-medium">Facebook</span>
              </a>
              <a 
                href={AGENT_INFO.socials.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 text-[#0A66C2] transition-colors"
              >
                <Linkedin className="h-5 w-5" />
                <span className="text-sm font-medium">LinkedIn</span>
              </a>
              <a 
                href={AGENT_INFO.socials.realtor}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#D92228]/10 hover:bg-[#D92228]/20 text-[#D92228] transition-colors"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18l6.9 3.45L12 11.08 5.1 7.63 12 4.18zM4 8.82l7 3.5v7.36l-7-3.5V8.82zm9 10.86v-7.36l7-3.5v7.36l-7 3.5z"/>
                </svg>
                <span className="text-sm font-medium">Realtor.com</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="section-padding bg-muted/30">
      <div className="container-main">
        <h2 className="heading-lg text-secondary text-center mb-12">What I Believe</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: Heart,
              title: "People Over Transactions",
              description: "Real estate is about relationships and helping families find where their lives will grow and thrive."
            },
            {
              icon: CheckCircle,
              title: "Honesty & Integrity",
              description: "Trust and clear communication are the foundations of every successful real estate experience."
            },
            {
              icon: Users,
              title: "Community First",
              description: "I'm honored to serve Aberdeen and the surrounding communities I truly care about."
            }
          ].map((value, i) => (
            <div key={i} className="card-feature text-center">
              <value.icon className="h-12 w-12 mx-auto mb-4 text-primary" />
              <h3 className="font-heading text-xl font-semibold mb-3">{value.title}</h3>
              <p className="text-muted-foreground">{value.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="section-padding bg-secondary text-white text-center">
      <div className="container-main">
        <h2 className="heading-lg text-white mb-6">Let's Work Together</h2>
        <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
          Ready to start your real estate journey? I'd love to hear from you.
        </p>
        <Link to="/contact">
          <Button className="btn-primary" data-testid="contact-cta-btn">
            Get In Touch
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Link>
      </div>
    </section>
  </div>
);

// Contact Page
const ContactPage = () => (
  <div className="pt-20" data-testid="contact-page">
    {/* Header */}
    <section className="bg-muted/30 py-16">
      <div className="container-main">
        <p className="caption-text text-primary mb-2">Get In Touch</p>
        <h1 className="heading-xl text-secondary mb-4">Let's Connect</h1>
        <p className="body-text max-w-2xl">
          Whether you're buying, selling, or just have questions about the Aberdeen real estate market, 
          I'm here to help. Reach out and let's start a conversation.
        </p>
      </div>
    </section>

    {/* Contact Content */}
    <section className="section-padding">
      <div className="container-main">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            {/* Melanie with Sign Photo */}
            <div className="mb-8">
              <img
                src={IMAGES.melanieWithSign}
                alt={`${AGENT_INFO.name} - ${AGENT_INFO.phone}`}
                className="w-full max-w-sm rounded-2xl shadow-lg"
              />
            </div>

            <div className="space-y-6 mb-12">
              <a 
                href={`tel:${AGENT_INFO.phone}`}
                className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors group"
                data-testid="contact-phone"
              >
                <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-semibold text-lg">{AGENT_INFO.phone}</p>
                </div>
              </a>
              <a 
                href={`mailto:${AGENT_INFO.email}`}
                className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors group"
                data-testid="contact-email"
              >
                <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-semibold text-lg">{AGENT_INFO.email}</p>
                </div>
              </a>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50">
                <div className="p-3 rounded-full bg-primary/10">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Serving</p>
                  <p className="font-semibold text-lg">Aberdeen, SD & Surrounding Areas</p>
                </div>
              </div>
            </div>

            {/* Office Hours */}
            <div className="p-6 rounded-xl border border-border">
              <h3 className="font-heading text-lg font-semibold mb-4">Office Hours</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Monday - Friday</span>
                  <span>9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Saturday</span>
                  <span>10:00 AM - 4:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Sunday</span>
                  <span>By Appointment</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-card rounded-2xl shadow-lg p-8 border border-border">
            <h2 className="font-heading text-2xl font-semibold mb-2">Send a Message</h2>
            <p className="text-muted-foreground mb-6">
              Fill out the form below and I'll get back to you as soon as possible.
            </p>
            <LeadCaptureForm source="contact_page" />
          </div>
        </div>
      </div>
    </section>
  </div>
);

// Redirect component for external links
const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  
  return (
    <div className="pt-20 min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">Redirecting to MLS listings...</p>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <SiteConfigProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <Navbar />
          <main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/properties" element={<ExternalRedirect to="https://www.hubcityhomes4sale.com/map/" />} />
              <Route path="/property/:id" element={<PropertyDetailPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
            </Routes>
          </main>
          <Footer />
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </div>
    </SiteConfigProvider>
  );
}

export default App;
